import React, { useState, useEffect } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { questionnaireApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const FeedbackQuestionnaire = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    section1_background: {
      q1_employment_status: '',
      q1_employment_status_other: '',
      q2_target_field: '',
      q3_jobs_applied_count: '',
      q4_application_methods: [],
      q4_application_methods_other: '',
      q5_app_feedback: ''
    },
    section2_resumemo_experience: {
      q1_plan: currentUser?.membership_type || 'Community',
      q2_feature_ratings: {
      },
      q3_most_helpful_feature: '',
      q4_confusing_feature: '',
      q5_resume_improvement: '',
      q6_job_confidence: ''
    },
    section3_membership_experience: {
      gold_subsection: {
        q1_membership_satisfaction: '',
        q2_price_acceptable: '',
        q2_price_reason: '',
        q3_gold_feature_most_valuable: ''
      },
      platinum_subsection: {
        q1_used_personalized_feedback: '',
        q2_concierge_support: '',
        q3_mock_interviews_interest: ''
      }
    },
    section4_technical_ux: {
      q1_technical_issues: '',
      q2_navigation_ease: '',
      q3_missing_features: ''
    },
    section5_future_potential: {
      q1_would_recommend: '',
      q2_next_feature_wish: '',
      q3_beta_testing_willing: '',
      q4_email: ''
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const totalPages = 5;

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleNestedInputChange = (section, nestedSection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [nestedSection]: { ...prev[section][nestedSection], [field]: value }
      }
    }));
  };

  const handleCheckboxChange = (method) => {
    setFormData(prev => ({
      ...prev,
      section1_background: {
        ...prev.section1_background,
        q4_application_methods: prev.section1_background.q4_application_methods.includes(method)
          ? prev.section1_background.q4_application_methods.filter(m => m !== method)
          : [...prev.section1_background.q4_application_methods, method]
      }
    }));
  };

  const validateCurrentPage = () => {
    const { section1_background, section2_resumemo_experience, section3_membership_experience, section4_technical_ux, section5_future_potential } = formData;
    
    switch (currentPage) {
      case 1:
        return section1_background.q1_employment_status &&
               section1_background.q2_target_field &&
               section1_background.q3_jobs_applied_count &&
               (section1_background.q4_application_methods || []).length > 0;
      case 2:
        return section2_resumemo_experience.q1_plan &&
               (section2_resumemo_experience.q3_most_helpful_feature || '').trim() &&
               (section2_resumemo_experience.q4_confusing_feature || '').trim() &&
               section2_resumemo_experience.q5_resume_improvement &&
               section2_resumemo_experience.q6_job_confidence;
      case 3:
        if (section2_resumemo_experience.q1_plan === 'Gold') {
          const gold = section3_membership_experience.gold_subsection || {};
          return gold.q1_membership_satisfaction &&
                 gold.q2_price_acceptable &&
                 (gold.q3_gold_feature_most_valuable || '').trim();
        } else if (section2_resumemo_experience.q1_plan === 'Platinum') {
          const plat = section3_membership_experience.platinum_subsection || {};
          return plat.q1_used_personalized_feedback &&
                 plat.q2_concierge_support &&
                 plat.q3_mock_interviews_interest;
        }
        return true; // Community plan has no required fields
      case 4:
        return section4_technical_ux.q2_navigation_ease &&
               (section4_technical_ux.q3_missing_features || '').trim();
      case 5:
        return section5_future_potential.q1_would_recommend &&
               (section5_future_potential.q2_next_feature_wish || '').trim() &&
               section5_future_potential.q3_beta_testing_willing;
      default:
        return true;
    }
  };

  const nextPage = () => {
    if (!validateCurrentPage()) {
      toast.error('Please fill in all required fields before proceeding.');
      return;
    }
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateCurrentPage()) {
      toast.error('Please fill in all required fields before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const processedData = {
        ...formData,
        section1_background: {
          ...formData.section1_background,
          q1_employment_status: formData.section1_background.q1_employment_status === 'Other' 
            ? formData.section1_background.q1_employment_status_other 
            : formData.section1_background.q1_employment_status,
          q4_application_methods: formData.section1_background.q4_application_methods.includes('Other')
            ? [...formData.section1_background.q4_application_methods.filter(m => m !== 'Other'), formData.section1_background.q4_application_methods_other]
            : formData.section1_background.q4_application_methods
        }
      };

      await questionnaireApi.createQuestionnaire('feedback-v2', processedData);
      toast.success('Thank you for your feedback! 300 credits have been added to your account.');
      onClose();
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSection1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Background Information</h3>
        <p className="text-gray-600">Help us understand your current situation</p>
      </div>
      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-base font-semibold text-gray-800 mb-4">
          What is your current employment status? *
        </label>
        <div className="space-y-3">
          {['Employed Full-time', 'Employed Part-time', 'Freelancer / Consultant', 'Student', 'Unemployed', 'Other'].map(option => (
            <label key={option} className="flex items-center p-3 bg-white rounded-md border hover:bg-primary-50 cursor-pointer">
              <input
                type="radio"
                name="employmentStatus"
                value={option}
                checked={formData.section1_background.q1_employment_status === option}
                onChange={(e) => handleInputChange('section1_background', 'q1_employment_status', e.target.value)}
                className="mr-3 text-primary-600"
              />
              <span className="text-gray-700">{option}</span>
            </label>
          ))}
        </div>
        {formData.section1_background.q1_employment_status === 'Other' && (
          <input
            type="text"
            placeholder="Please specify"
            value={formData.section1_background.q1_employment_status_other}
            onChange={(e) => handleInputChange('section1_background', 'q1_employment_status_other', e.target.value)}
            className="mt-3 w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        )}
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-base font-semibold text-gray-800 mb-4">
          Which field/industry are you targeting for your next job? *
        </label>
        <input
          type="text"
          value={formData.section1_background.q2_target_field}
          onChange={(e) => handleInputChange('section1_background', 'q2_target_field', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-700"
          placeholder="e.g., Software Engineering, Marketing, Finance"
        />
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-base font-semibold text-gray-800 mb-4">
          How many jobs have you applied for in the past 3 months? *
        </label>
        <div className="space-y-3">
          {['0', '1–5', '6–15', '16+'].map(option => (
            <label key={option} className="flex items-center p-3 bg-white rounded-md border hover:bg-primary-50 cursor-pointer">
              <input
                type="radio"
                name="jobsApplied"
                value={option}
                checked={formData.section1_background.q3_jobs_applied_count === option}
                onChange={(e) => handleInputChange('section1_background', 'q3_jobs_applied_count', e.target.value)}
                className="mr-3 text-primary-600"
              />
              <span className="text-gray-700">{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-base font-semibold text-gray-800 mb-4">
          How do you typically apply for jobs? * (Select all that apply)
        </label>
        <div className="space-y-3">
          {['LinkedIn', 'Indeed', 'Company Websites', 'Referrals', 'Recruiters', 'Other'].map(method => (
            <label key={method} className="flex items-center p-3 bg-white rounded-md border hover:bg-primary-50 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.section1_background.q4_application_methods.includes(method)}
                onChange={() => handleCheckboxChange(method)}
                className="mr-3 text-primary-600"
              />
              <span className="text-gray-700">{method}</span>
            </label>
          ))}
        </div>
        {formData.section1_background.q4_application_methods.includes('Other') && (
          <input
            type="text"
            placeholder="Please specify"
            value={formData.section1_background.q4_application_methods_other}
            onChange={(e) => handleInputChange('section1_background', 'q4_application_methods_other', e.target.value)}
            className="mt-3 w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        )}
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-base font-semibold text-gray-800 mb-4">
          Do you have any feedback about this app?
        </label>
        <textarea
          value={formData.section1_background.q5_app_feedback}
          onChange={(e) => handleInputChange('section1_background', 'q5_app_feedback', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-700"
          placeholder="Share your thoughts about the app (optional)"
          rows={4}
        />
      </div>
    </div>
  );

  const renderSection2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Your ResuMemo Experience</h3>
        <p className="text-gray-600">Tell us about your experience with our features</p>
      </div>
      <div className="bg-primary-50 p-6 rounded-lg border border-primary-200">
        <label className="block text-base font-semibold text-gray-800 mb-2">
          Your Current Plan
        </label>
        <div className="flex items-center p-4 bg-white rounded-lg border-2 border-primary-300">
          <div className="w-3 h-3 bg-primary-600 rounded-full mr-3"></div>
          <span className="text-gray-800 font-bold text-lg">{currentUser?.membership_type || 'Community'}</span>
          <span className="ml-2 px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">Auto-detected</span>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <label className="block text-base font-semibold text-gray-800 mb-4">
          How would you rate the following features? (1 = Not useful at all, 5 = Extremely useful) *
        </label>
        <div className="space-y-7">
          {[
            { key: 'careerDirection', label: 'Career Direction' },
            { key: 'courseProject', label: 'Course Projects to Resume' },
            { key: 'aiRephrasing', label: 'AI Rephrasing Suggestions' },
            { key: 'jobBoard', label: 'Job Board' },
            { key: 'jobFitScoring', label: 'Job Fit Scoring' },
            { key: 'resumeTailoring', label: 'Resume Tailoring to Job Descriptions' },
            { key: 'versionTracking', label: 'Resume Version Tracking' },
            { key: 'resumeTemplates', label: 'Resume Templates' },
            { key: 'jobTracker', label: 'Job Tracker' },
            { key: 'dashboardExperience', label: 'Dashboard Experience' },
            { key: 'visualDesign', label: 'Visual Design / Aesthetics' }
          ].map(feature => (
            <div key={feature.key} className="bg-white p-4 rounded-md border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span className="text-gray-700 font-medium">{feature.label}</span>
                <div className="flex space-x-4">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <label key={rating} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={feature.key}
                        value={rating}
                        checked={formData.section2_resumemo_experience.q2_feature_ratings[feature.key] === rating.toString()}
                        onChange={(e) => handleNestedInputChange('section2_resumemo_experience', 'q2_feature_ratings', feature.key, e.target.value)}
                        className="mr-2 text-primary-600"
                      />
                      <span className="text-gray-700">{rating}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Which feature did you find the most helpful, and why? *
        </label>
        <textarea
          value={formData.section2_resumemo_experience.q3_most_helpful_feature}
          onChange={(e) => handleInputChange('section2_resumemo_experience', 'q3_most_helpful_feature', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Was there any feature you found confusing or not intuitive? *
        </label>
        <textarea
          value={formData.section2_resumemo_experience.q4_confusing_feature}
          onChange={(e) => handleInputChange('section2_resumemo_experience', 'q4_confusing_feature', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How well did ResuMemo help you improve your resume? *
        </label>
        <div className="space-y-2">
          {['Not at all', 'A little', 'Somewhat', 'Very much', 'Completely transformed it'].map(option => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name="resumeImprovement"
                value={option}
                checked={formData.section2_resumemo_experience.q5_resume_improvement === option}
                onChange={(e) => handleInputChange('section2_resumemo_experience', 'q5_resume_improvement', e.target.value)}
                className="mr-2"
              />
              {option}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Did ResuMemo help you feel more confident when applying for jobs? *
        </label>
        <div className="space-y-2">
          {['Yes', 'No', 'Not sure'].map(option => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name="jobConfidence"
                value={option}
                checked={formData.section2_resumemo_experience.q6_job_confidence === option}
                onChange={(e) => handleInputChange('section2_resumemo_experience', 'q6_job_confidence', e.target.value)}
                className="mr-2"
              />
              {option}
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSection3 = () => {
    if (formData.section2_resumemo_experience.q1_plan === 'Gold') {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium mb-4">Section 3 - Gold Membership Experience</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How satisfied are you with the additional features in Gold vs. Community? *
            </label>
            <div className="space-y-2">
              {['Much better', 'Slightly better', 'About the same', 'Not worth the upgrade'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="membershipSatisfaction"
                    value={option}
                    checked={formData.section3_membership_experience.gold_subsection.q1_membership_satisfaction === option}
                    onChange={(e) => handleNestedInputChange('section3_membership_experience', 'gold_subsection', 'q1_membership_satisfaction', e.target.value)}
                    className="mr-2"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Was the price point acceptable to you for what you received? *
            </label>
            <div className="space-y-2">
              {['Yes', 'No'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="priceAcceptable"
                    value={option}
                    checked={formData.section3_membership_experience.gold_subsection.q2_price_acceptable === option}
                    onChange={(e) => handleNestedInputChange('section3_membership_experience', 'gold_subsection', 'q2_price_acceptable', e.target.value)}
                    className="mr-2"
                  />
                  {option}
                </label>
              ))}
            </div>
            {formData.section3_membership_experience.gold_subsection.q2_price_acceptable === 'No' && (
              <textarea
                placeholder="If no, please specify"
                value={formData.section3_membership_experience.gold_subsection.q2_price_reason}
                onChange={(e) => handleNestedInputChange('section3_membership_experience', 'gold_subsection', 'q2_price_reason', e.target.value)}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Which Gold-exclusive feature did you find most valuable? *
            </label>
            <textarea
              value={formData.section3_membership_experience.gold_subsection.q3_gold_feature_most_valuable}
              onChange={(e) => handleNestedInputChange('section3_membership_experience', 'gold_subsection', 'q3_gold_feature_most_valuable', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
            />
          </div>
        </div>
      );
    } else if (formData.section2_resumemo_experience.q1_plan === 'Platinum') {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium mb-4">Section 3 - Platinum Membership Experience</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Did you use the personalized 1-on-1 feedback or resume audit service? *
            </label>
            <div className="space-y-2">
              {['Yes', 'No', 'Not yet but I plan to'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="usedPersonalizedFeedback"
                    value={option}
                    checked={formData.section3_membership_experience.platinum_subsection.q1_used_personalized_feedback === option}
                    onChange={(e) => handleNestedInputChange('section3_membership_experience', 'platinum_subsection', 'q1_used_personalized_feedback', e.target.value)}
                    className="mr-2"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Was the concierge-level support helpful for your career planning? *
            </label>
            <div className="space-y-2">
              {['Extremely helpful', 'Moderately helpful', 'Not helpful'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="conciergeSupport"
                    value={option}
                    checked={formData.section3_membership_experience.platinum_subsection.q2_concierge_support === option}
                    onChange={(e) => handleNestedInputChange('section3_membership_experience', 'platinum_subsection', 'q2_concierge_support', e.target.value)}
                    className="mr-2"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              If Platinum included mock interviews or LinkedIn optimization, would you use it? *
            </label>
            <div className="space-y-2">
              {['Yes', 'No', 'Maybe – depending on the job role'].map(option => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="mockInterviewsInterest"
                    value={option}
                    checked={formData.section3_membership_experience.platinum_subsection.q3_mock_interviews_interest === option}
                    onChange={(e) => handleNestedInputChange('section3_membership_experience', 'platinum_subsection', 'q3_mock_interviews_interest', e.target.value)}
                    className="mr-2"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium mb-4">Section 3 - Community Experience</h3>
          <p className="text-gray-600">Thank you for using our Community plan! Please continue to the next section.</p>
        </div>
      );
    }
  };

  const renderSection4 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium mb-4">Section 4 - Technical & UX</h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Did you experience any technical issues or bugs? If yes, please describe.
        </label>
        <textarea
          value={formData.section4_technical_ux.q1_technical_issues}
          onChange={(e) => handleInputChange('section4_technical_ux', 'q1_technical_issues', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How easy was it to navigate the platform? *
        </label>
        <div className="flex items-center space-x-4">
          <span className="text-sm">Very difficult</span>
          <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map(rating => (
              <label key={rating} className="flex items-center">
                <input
                  type="radio"
                  name="navigationEase"
                  value={rating}
                  checked={formData.section4_technical_ux.q2_navigation_ease === rating.toString()}
                  onChange={(e) => handleInputChange('section4_technical_ux', 'q2_navigation_ease', e.target.value)}
                  className="mr-1"
                />
                {rating}
              </label>
            ))}
          </div>
          <span className="text-sm">Very easy</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Is there anything that felt missing or incomplete in your user journey? *
        </label>
        <textarea
          value={formData.section4_technical_ux.q3_missing_features}
          onChange={(e) => handleInputChange('section4_technical_ux', 'q3_missing_features', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
        />
      </div>
    </div>
  );

  const renderSection5 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium mb-4">Section 5 - Future Potential</h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Would you recommend ResuMemo to others? *
        </label>
        <div className="space-y-2">
          {['Definitely', 'Probably', 'Not sure', 'Probably not', 'Definitely not'].map(option => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name="wouldRecommend"
                value={option}
                checked={formData.section5_future_potential.q1_would_recommend === option}
                onChange={(e) => handleInputChange('section5_future_potential', 'q1_would_recommend', e.target.value)}
                className="mr-2"
              />
              {option}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          What's the #1 feature you'd love to see in the next version? *
        </label>
        <textarea
          value={formData.section5_future_potential.q2_next_feature_wish}
          onChange={(e) => handleInputChange('section5_future_potential', 'q2_next_feature_wish', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Are you willing to be contacted for future beta testing? *
        </label>
        <div className="space-y-2">
          {['Yes', 'No', 'Maybe'].map(option => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name="betaTestingWilling"
                value={option}
                checked={formData.section5_future_potential.q3_beta_testing_willing === option}
                onChange={(e) => handleInputChange('section5_future_potential', 'q3_beta_testing_willing', e.target.value)}
                className="mr-2"
              />
              {option}
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your email (optional, if you'd like us to follow up):
        </label>
        <input
          type="email"
          value={formData.section5_future_potential.q4_email}
          onChange={(e) => handleInputChange('section5_future_potential', 'q4_email', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="your.email@example.com"
        />
      </div>
    </div>
  );

  const getCurrentSection = () => {
    switch (currentPage) {
      case 1: return renderSection1();
      case 2: return renderSection2();
      case 3: return renderSection3();
      case 4: return renderSection4();
      case 5: return renderSection5();
      default: return renderSection1();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center p-8 border-b bg-gradient-to-r from-primary-50 to-primary-100">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">ResuMemo Feedback Survey</h2>
            <p className="text-lg text-gray-600 mt-1">Page {currentPage} of {totalPages}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-white rounded-full transition-colors" aria-label="Close feedback survey">
            <FiX className="text-2xl" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8">
          <div className="min-h-[500px]">
            {getCurrentSection()}
          </div>
          
          <div className="flex justify-between items-center pt-8 border-t mt-8 bg-gray-50 -mx-8 px-8 py-6">
            <button
              type="button"
              onClick={prevPage}
              disabled={currentPage === 1}
              className="btn-outline btn-md flex items-center"
              aria-label="Previous page"
            >
              <FiChevronLeft className="mr-2" /> Previous
            </button>
            
            <div className="flex space-x-3">
              {Array.from({ length: totalPages }, (_, i) => (
                <div
                  key={i + 1}
                  className={`w-4 h-4 rounded-full transition-all duration-200 ${
                    currentPage === i + 1 ? 'bg-primary-600 scale-125' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            {currentPage < totalPages ? (
              <button
                type="button"
                onClick={nextPage}
                className="btn-primary btn-md flex items-center"
                aria-label="Next page"
              >
                Next <FiChevronRight className="ml-2" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary btn-lg"
                aria-label="Submit feedback and get 300 credits"
              >
                {isSubmitting ? 'Submitting...' : 'Submit & Get 300 Credits'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackQuestionnaire;